import React, { createContext, useEffect } from "react";
import styled from "styled-components";
import { Location } from "@reach/router";
import { View, Text, Divider, Link, A, Icon } from "./components";
import { useToggle } from "react-hooks-lib";
import ArtistsIndex, { Container as AgencyContainer } from "./ArtistsIndex";
import CloseIcon from "./components/CloseIcon";
import Logo from "./components/Logo";
import { useMedia } from "use-media";
import OverlayMenuMobile from "./OverlayMenuMobile";

import * as data from "./data";

const OverlayMenuContext = createContext();

export const Consumer = OverlayMenuContext.Consumer;

const Container = styled(View).attrs(() => ({
  px: 3,
  bg: "#EEE",
}))`
  position: fixed;
  padding-top: calc(4vh + 4vw);
  padding-bottom: calc(4vh + 4vw);
  left: 0;
  right: 0;
  top: 0;
  height: calc(100vh + 32px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  opacity: 0;
  pointer-events: none;
  visibility: visible;
  ${(props) =>
    props.visible &&
    `
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  `} transition: opacity 250ms ease;
  transform: translate3d(0, 0, 0);
  z-index: 998;
`;

const Content = styled(View).attrs(() => ({ maxWidth: false, center: true }))`
  max-width: 1320px;
  @media (max-width: 1280px) {
    /* max-width: calc(100vw - 40px); */
    /* max-width: 100%; */
  }
  display: flex;
  & > *:first-child {
    width: calc(${(100 / 6) * 1.4}vw - 30px);
  }
  & > *:nth-child(2) {
    width: calc(${(100 / 6) * 4}vw - 30px);
  }
  justify-content: center;
`;

const ColumnBlock = styled(View).attrs(() => ({ pr: 4 }))`
  display: inline-block;
  width: 100%;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  * {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
  }
`;

const Toggle = ({ location, on, toggle }) => {
  useEffect(() => {
    if (on) {
      toggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  return null;
};

const ContentContent = styled(View).attrs(() => ({ pt: 3 }))`
  padding-right: 12vw;
  @media (max-width: 1280px) {
    padding-right: 3em;
  }
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const LinkThing = ({ children, href }) => (
  <Link
    my={1}
    textStyle="label"
    invert
    as="a"
    href={href}
    style={{ display: "block" }}
  >
    {children}
  </Link>
);

const OverlayMenu = ({ children }) => {
  const { on, toggle, reset } = useToggle(false);
  const isWide = useMedia({ minWidth: 800 });
  return (
    <OverlayMenuContext.Provider value={{ on, toggle, reset }}>
      <Location>
        {({ location }) => (
          <Toggle location={location} on={on} toggle={toggle} />
        )}
      </Location>
      {children}
      {isWide ? (
        <Container visible={on} px={3}>
          <CloseIcon
            onClick={toggle}
            style={{
              position: "fixed",
              left: "50%",
              top: 20,
              marginLeft: -15,
            }}
          />
          <Content>
            <ContentContent>
              <View>
                <Link block to="/">
                  <Logo />
                </Link>
                <Divider mt={3} />
                <Link invert to="/">
                  News
                </Link>
                <Divider />
                <Link invert to="/artists">
                  Artists
                </Link>
                <Divider />
                <Link
                  invert
                  to="/education"
                  as="a"
                  href="https://www.houseoforangeeducation.nl/"
                  target="_blank"
                >
                  Education
                </Link>
                <Divider />
                <Link invert to="/contact">
                  Contact
                </Link>
                <Divider />
              </View>
              <View>
                <Text textStyle="bread">
                  <p>
                    <Link
                      invert
                      as="a"
                      target="_blank"
                      href={data.googleMapsUrl}
                    >
                      Leidsegracht 6H
                      <br />
                      1016 CK Amsterdam,
                      <br />
                      The Netherlands
                    </Link>
                  </p>
                  <p>
                    <Link invert as="a" href="mailto:info@houseoforange.nl">
                      info@houseoforange.nl
                    </Link>
                    <br />
                    <Link invert as="a" href="tel:+31206223303">
                      +31 (0)20 622 3303
                    </Link>
                  </p>
                  <View mt={4} mb={1}>
                    <A
                      href="https://www.facebook.com/Houseoforange"
                      target="_blank"
                    >
                      <Icon icon="facebook" mr={3} />
                    </A>
                    <A
                      href="https://www.instagram.com/houseoforangeagency"
                      target="_blank"
                    >
                      <Icon icon="instagram" />
                    </A>
                  </View>
                </Text>
              </View>
            </ContentContent>
            <View>
              <Divider mt={3} mb={[2, 2, 2]} />
              <Text textStyle="headline">Artists</Text>
              <Divider mb={[2, 3, 3]} />
              <ArtistsIndex />
              <Divider mt={4} mb={[2, 2, 2]} />
              <Text textStyle="headline">Agency</Text>
              <Divider mb={[2, 3, 3]} />
              <AgencyContainer style={{ marginBottom: 0, maxHeight: "7.6rem" }}>
                <ColumnBlock>
                  <Text textStyle="title" mt={3}>
                    Agents
                  </Text>
                  <Divider />
                  <LinkThing href="mailto:erik@houseoforange.nl">
                    Erik van Dillen
                  </LinkThing>
                  <LinkThing href="mailto:manon@houseoforange.nl">
                    Manon Meijerman
                  </LinkThing>
                </ColumnBlock>
                <ColumnBlock>
                  <Text textStyle="title" mt={3}>
                    Finance
                  </Text>
                  <Divider />
                  <Link
                    mt={1}
                    textStyle="label"
                    invert
                    as="a"
                    href="mailto:nina@houseoforange.nl"
                    style={{ display: "block" }}
                  >
                    Nina Oliver
                  </Link>
                  <Link
                    mt={1}
                    textStyle="label"
                    invert
                    as="a"
                    href="mailto:solange@houseoforange.nl"
                    style={{ display: "block" }}
                  >
                    Solange Clercx
                  </Link>
                  <Link
                    mt={1}
                    textStyle="label"
                    invert
                    as="a"
                    href="mailto:finance@houseoforange.nl"
                    style={{ display: "block" }}
                  >
                    finance@houseoforange.nl
                  </Link>
                </ColumnBlock>
                <ColumnBlock>
                  <Text textStyle="title" mt={3}>
                    &nbsp;
                  </Text>
                </ColumnBlock>
                <ColumnBlock>
                  <Text textStyle="title" mt={3}>
                    Terms and Conditions
                  </Text>
                  <Divider />
                  <Link
                    as="a"
                    href="/House-of-Orange-Agency-BV-T&C.pdf"
                    my={1}
                    textStyle="label"
                    invert
                  >
                    Hair, Make Up & Styling
                  </Link>
                  <br />
                  <Link
                    as="a"
                    href="/House-of-Orange-Agency-BV-general-purchase-conditions.pdf"
                    my={1}
                    textStyle="label"
                    invert
                  >
                    General Purchase
                  </Link>
                  <br />
                  <Link
                    as="a"
                    href="/House-of-Orange_Makeup_School_AVG_2023.pdf"
                    my={1}
                    textStyle="label"
                    invert
                  >
                    Privacy / AVG
                  </Link>
                </ColumnBlock>
              </AgencyContainer>
            </View>
          </Content>
        </Container>
      ) : (
        <OverlayMenuMobile on={on} toggle={toggle} />
      )}
    </OverlayMenuContext.Provider>
  );
};

export default OverlayMenu;
